import { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cards from "../../components/Cards";
import { FaCalendarCheck, FaChartLine, FaHourglassHalf } from "react-icons/fa";
import { TbSortAscending } from "react-icons/tb";
import { useAuth } from "../../utils/AuthContext";
import { Link } from "react-router-dom";
import { TiArrowRight } from "react-icons/ti";
import DoctorPerformanceChart from "../../components/DoctorPerformanceChart";
import DoctorApptStatusChart from "../../components/DoctorApptStatusChart";
import TechnicalSupportNotice from "../../components/TechnicalSupportNotice";

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch (e) {
        return "Invalid Date";
    }
};

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const DoctorDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [doctorsData, setDoctorsData] = useState([]); 
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myRecentAppointments, setMyRecentAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);

 useEffect(() => {
        if (!user?.mobile) {
          setLoading(false);
          console.log("Dashboard: Waiting for user mobile number...");
          return;
      }
        console.log(user)

        const loadDoctorData = async () => {
            setLoading(true);
            try {
                const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });

                const today = new Date().toISOString().split("T")[0];
                const now = new Date();
                const year = now.getFullYear();
                const month = now.getMonth();

                const [
                    doctorsRes,
                    appointmentsRes,
                ] = await Promise.all([
                    api.get(`/doctor`).catch(() => ({ data: [] })),
                    api.get(`/appointment`).catch(() => ({ data: { appointments: [] } })),
                ]);

                const fetchedDoctors = doctorsRes.data?.data || doctorsRes.data || [];
                setDoctorsData(fetchedDoctors);

                let userMobileForMatch = user.mobile;
                if (userMobileForMatch && userMobileForMatch.startsWith('88')) {
                    userMobileForMatch = userMobileForMatch.substring(2);
                }

               const myDoctorProfile = fetchedDoctors.find(
                 doc => doc.translations?.en?.contactNumberSerial === userMobileForMatch
               );
              const loggedInDoctorId = myDoctorProfile.id;
              const allAppointments = appointmentsRes.data?.appointments || [];
              const myAppointments = allAppointments.filter(app => app.doctorId === loggedInDoctorId); 
              const myPendingAppointments = myAppointments.filter(app => app.status === 'pending');
              const totalPendingCount = myPendingAppointments.length;

                // Card 2 Data
                const myAppointmentsToday = myAppointments.filter(app => 
                    app.appointmentDate && app.appointmentDate.startsWith(today)
                );
                const totalToday = myAppointmentsToday.length;
                const pendingTodayCount = myAppointmentsToday.filter(app => app.status === 'pending').length;
                const approvedToday = myAppointmentsToday.filter(app => app.status === 'approved').length;

                // Card 3 Data
                const myAppointmentsThisMonth = myAppointments.filter(app => {
                    if (!app.appointmentDate) return false;
                    const appDate = new Date(app.appointmentDate);
                    return appDate.getFullYear() === year && appDate.getMonth() === month;
                });
                const myApprovedAppointmentsThisMonth = myAppointmentsThisMonth.filter(
                    app => app.status === 'approved'
                );
                const totalFeesMonth = myApprovedAppointmentsThisMonth.reduce((sum, app) => {
                    return sum + (app.consultationFee || 0);
                }, 0);
                const totalPatientsMonth = myApprovedAppointmentsThisMonth.length;

                // --- Set Card Data ---
                const cards = [
                   {
                        category: "Pending Appointments",
                        icon: <FaHourglassHalf className="text-orange-500 text-xl" />,
                        total: totalPendingCount,
                        status: "Awaiting Review",
                        cardListDetails: [
                            { label: "Pending Today", value: pendingTodayCount },
                            { label: "Pending Future", value: totalPendingCount - pendingTodayCount },
                        ],
                        link: "/my-appointments?status=pending"
                    },
                   {
                        category: "All Appointments Today",
                        icon: <FaCalendarCheck className="text-blue-500 text-xl" />,
                        total: totalToday, status: "Scheduled Today",
                        cardListDetails: [
                            { label: "Pending", value: pendingTodayCount },
                            { label: "Approved", value: approvedToday },
                        ], link: "/my-appointments"
                    },
                    {
                        category: "My Monthly Performance",
                        icon: <FaChartLine className="text-purple-500 text-xl" />,
                        total: `৳${totalFeesMonth.toLocaleString()}`, status: "Approved Earnings",
                        cardListDetails: [
                            { label: "Approved Patients", value: totalPatientsMonth },
                            { label: "Total Fees Earned", value: `৳${totalFeesMonth.toLocaleString()}` }
                        ]
                    },
                ];
                setDashboardData(cards);
                const sortedAppointments = [...myAppointments].sort((a, b) => 
                    new Date(b.appointmentDate || b.createdAt) - new Date(a.appointmentDate || a.createdAt)
                );
                setMyRecentAppointments(sortedAppointments.slice(0, 5));

                const upcoming = myAppointments
                    .filter(app => {
                        if (!app.appointmentDate) return false;
                        const appDate = new Date(app.appointmentDate);
                        appDate.setHours(0,0,0,0); 
                        return (app.status === 'approved' || app.status === 'pending') && appDate >= now;
                    })
                    .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));

                setUpcomingAppointments(upcoming.slice(0, 5));

            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadDoctorData();
    }, [user]);

  const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Approved</span>;
            case 'pending':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-600">Pending</span>;
            case 'cancelled':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-600">Cancelled</span>;
            default:
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">{status || 'Unknown'}</span>;
        }
    };

  return (
    <div className="md:py-4">
       {/* Top Title Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="font-semibold text-lg">
          Welcome back, {user?.name || "Doctor"} 👋
        </h1>
        <div>
          <Link to="/book-appointment">
            <button className="py-2 px-4 bg-[#73CB64] hover:bg-green-700 text-white rounded-md flex items-center gap-2 text-sm">
              <TbSortAscending /> Add Appointment
            </button>
          </Link>
        </div>
      </div>
      <div className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} height={150} />
            ))
          ) : (
            dashboardData.map((card, i) => (
              <Cards key={i} data={card} isEven={i % 2 === 0} showLinkIcon={true} />
            ))
          )}
        </div>
      </div>
      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6">
        <TechnicalSupportNotice />
        {/* Patients vs Earnings Stats */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">
              My Monthly Performance (Patients vs Earnings)
            </h2>
            <ul className="flex gap-2">
              {["All", "1M", "6M", "1Y"].map((t) => (
                <li
                  key={t}
                  className="w-9 py-1 text-center text-sm bg-gray-100 hover:bg-gray-200 rounded-sm cursor-pointer"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-4">
            {/* Use the new chart component */}
            {loading ? (
            <Skeleton height={400} />
          ) : (
            <DoctorPerformanceChart user={user} />
          )}
        </div>
        </div>
      </div>
      {/* Top Doctors & Breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6 mt-6">
        <div className="col-span-2 bg-white shadow rounded-lg">
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="font-medium">Recent Appointment Bills</h2>
        <Link to="/my-appointments">
          <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
            View All <TiArrowRight />
          </button>
        </Link>
      </div>
      <div className="p-4 overflow-x-auto">
      {loading ? (
          <Skeleton height={200} />
          ) : (
              <table className="min-w-full w-full divide-y divide-gray-200">
                  <thead>
                      <tr className="bg-gray-50">
                          <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Appointment ID</th>
                          <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                          <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                          <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Fee (৳)</th>
                          <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {myRecentAppointments.length > 0 ? (
                          myRecentAppointments.map((appt) => (
                              <tr key={appt.id} className="hover:bg-gray-50">
                                  <td className="p-3 text-sm text-gray-700">{appt.id || '-'}</td>
                                  <td className="p-3 text-sm text-gray-700">{appt.patientName || appt.patient?.name || 'N/A'}</td>
                                  <td className="p-3 text-sm text-gray-700">
                                      {formatDate(appt.appointmentDate || appt.createdAt)}
                                  </td>
                                  <td className="p-3 text-sm text-gray-700">{appt.consultationFee?.toLocaleString() || '0'}</td>
                                  <td className="p-3 text-sm text-gray-700">{getStatusBadge(appt.status)}</td>
                              </tr>
                          ))
                      ) : (
                          <tr>
                              <td colSpan="5" className="text-center py-5 text-gray-500">
                                  No recent appointments found.
                              </td>
                          </tr>
                      )}
                  </tbody>
              </table>
          )}
      </div>
    </div>
        {/* Monthly Appointment Status Chart */}
        <div className="bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">Monthly Appointment Status</h2>
          </div>
          <div className="p-5">
            {loading ? (
              <Skeleton height={400} />
            ) : (
              <DoctorApptStatusChart user={user} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;